import type { Chord } from '@tunasong/music-lib'
import { chordToEvents } from './chord-to-events.js'
import type { Stab } from './stab-scheduler.js'

/**
 * Form-aware audition primitive. Pure function — emits a chronologically
 * ordered list of `Stab`s for an arbitrary sequence of sections at a given
 * tempo. Reuses `chordToEvents` for the per-chord NoteEvent shape (matches
 * the chord-stab convention used across Reharm Lab, Cadence Lab, etc.).
 *
 * Per-chord timing model:
 *  - Each chord plays as a half-note stab (`'2n'` duration).
 *  - Each chord occupies `measuresInSection / progression.length` measures.
 *  - At 100 BPM that's 2400 ms per measure-worth, so a 4-chord-over-8-measures
 *    section yields one stab every 4800 ms.
 *
 * Section-boundary gap model:
 *  - 50 ms pre-roll before every section except the first.
 *  - 200 ms post-roll after every section except the last.
 *  - Combined 250 ms silence-gap between adjacent sections — perceptually
 *    substitutes for the amplitude fade-in/fade-out specified by Bowie's
 *    memo. The half-note stab's natural release supplies the actual taper.
 *
 * Spec: docs/superpowers/specs/2026-05-23-form-visualizer-v1-design.md §5
 */

const QUARTERS_PER_MEASURE = 4
const PRE_ROLL_MS = 50
const POST_ROLL_MS = 200
const SECTION_GAP_MS = PRE_ROLL_MS + POST_ROLL_MS // 250 ms total between adjacent sections

const CHORD_DURATION = '2n' as const // half-note stab

export interface Section {
  /** Sequence of chords played end-to-end through the section. */
  progression: readonly Chord[]
  /**
   * Total measures this section occupies. Each chord in `progression` divides
   * `measuresInSection` evenly: chord time = `(measuresInSection / progression.length)` measures.
   */
  measuresInSection: number
  /** Stable id used for visual highlighting + UI section lookup. */
  kind: string
  /** Stable index in the form's section list — used by the highlight callback. */
  index: number
}

export interface SectionSequencerInput {
  sections: readonly Section[]
  /** BPM — v1 locks this at 100. */
  bpm: number
}

/** Pure stab-emitter. Deterministic given `sections` + `bpm`. */
export const buildSectionSequencerStabs = (input: SectionSequencerInput): Stab[] => {
  const { sections, bpm } = input
  if (sections.length === 0) {
    return []
  }
  const quarterMs = 60000 / bpm
  const measureMs = QUARTERS_PER_MEASURE * quarterMs

  const stabs: Stab[] = []
  let cursorMs = 0

  for (let s = 0; s < sections.length; s++) {
    const section = sections[s]
    if (s > 0) {
      cursorMs += SECTION_GAP_MS
    }
    const sectionMs = section.measuresInSection * measureMs
    const chordSlotMs = sectionMs / section.progression.length

    for (let c = 0; c < section.progression.length; c++) {
      stabs.push({
        events: chordToEvents(section.progression[c], CHORD_DURATION),
        delayMs: cursorMs + c * chordSlotMs,
      })
    }
    cursorMs += sectionMs
  }

  return stabs
}
